import React from 'react';
import { Box } from '@mui/material';
import { IconWrapper, InfoCard, SparkAccordion } from 'sharedComponents/UI';
import SubAccordionIconWrapper from './SubAccordionIconWrapper';
import TaggedParameter from './TaggedParameter';
import type {
  SparkReferenceInputParameter,
  SparkReferenceValue,
} from 'types/SparkStepReference';

type InputReferenceAccordionProps = {
  inputReference: SparkReferenceInputParameter[] | undefined;
  onTagClick: (value: SparkReferenceValue) => void;
};

const safeParseJson = (str: string) => {
  try {
    const parsed = JSON.parse(str);
    return parsed;
  } catch {
    return null;
  }
};

function InputReferenceAccordion({
  inputReference = [],
  onTagClick,
}: InputReferenceAccordionProps) {
  const grouped = inputReference.reduce<
    Record<string, SparkReferenceInputParameter[]>
  >((acc, curr) => {
    if (acc[curr.pageName]) {
      acc[curr.pageName] = [...acc[curr.pageName], curr];
    } else {
      acc[curr.pageName] = [curr];
    }

    return acc;
  }, {});

  return (
    <>
      {inputReference?.length > 0 ? (
        <>
          {Object.entries(grouped)?.map(
            ([pageName, inputReferenceByPage], index) => (
              <SparkAccordion
                title={pageName?.replace(/^page(\d+)$/, 'Page $1') || ''}
                icon={
                  <SubAccordionIconWrapper>
                    <IconWrapper size='small' color='var( --color-primary)'>
                      {index + 1}
                    </IconWrapper>
                  </SubAccordionIconWrapper>
                }
                key={pageName}
                summaryStyles={{
                  cursor: 'pointer',
                  padding: 0,
                }}
                detailsStyle={{ paddingBottom: 2 }}
                sx={{
                  '&:hover': {
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                }}
                expandedSx={{
                  width: '100%',
                }}
              >
                <Box display='flex' flexDirection='column' gap={1} width='100%'>
                  {inputReferenceByPage.flatMap((page) => (
                    <>
                      {(page.meta.referenceValues || []).map((value, index) => {
                        let fileTypeTag;
                        let fileName = value.value;
                        if (value.resource === 'files') {
                          const parsed = safeParseJson(value.value);

                          if (
                            Array.isArray(parsed) &&
                            parsed.length > 0 &&
                            parsed[0].name
                          ) {
                            fileName = parsed[0].name;

                            const lastDotIndex = fileName.lastIndexOf('.');
                            if (lastDotIndex !== -1) {
                              fileTypeTag = fileName
                                .substring(lastDotIndex + 1)
                                .toLowerCase();
                            }
                          }
                        }

                        return (
                          <TaggedParameter
                            label={value.key}
                            output={
                              value.resource === 'files'
                                ? fileName
                                : value.value
                            }
                            onClick={() => {
                              onTagClick(value);
                            }}
                            key={`${value.key}_${index}`}
                            iconURL={fileTypeTag ? '' : value.iconURL}
                            fileTypeTag={fileTypeTag}
                          />
                        );
                      })}
                    </>
                  ))}
                </Box>
              </SparkAccordion>
            )
          )}
        </>
      ) : (
        <InfoCard info='No Inputs' type='warning' />
      )}
    </>
  );
}

export default InputReferenceAccordion;
